import {
  ChangeDetectorRef,
  Component,
  Input,
  Inject,
  Signal,
} from '@angular/core';
import {
  ColorBackground,
  ColorForeground,
  Size,
} from '@ansys-internal/awc-angular/core';
import {
  AnalyticEventEnum,
  ConceptSections,
  ConceptSubSection,
} from '../../../shared/enums';
import { requirementSections } from '../../../shared/models/sections.model';
import {
  IconComponent,
  Icons,
  IconSize,
} from '@ansys-internal/awc-angular/icon';
import {
  ButtonComponent,
  ButtonSize,
  ButtonType,
} from '@ansys-internal/awc-angular/button';
import { DashboardPanelComponent } from '@ansys-internal/awf-angular/dashboard';
import { CommonModule, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { ResultsListComponent } from '../../widgets/results-list/results-list.component';
import {
  ActiveSectionState,
  ActiveTabState,
  SectionSelectAction,
} from '@ansys-internal/awf-angular/workspace';
import { JobLoadingStatus } from '../../../shared/enums/job-loading-status.enum';
import { RequirementsService } from '../../../shared/services/requirements.service';
import { ResultsService } from '../../../shared/services/results.services';
import { environment } from 'src/environments/environment';
import { EnvironmentType } from 'src/environments/environment.enum';
import { ActiveConceptState } from '../../../state/lib/active-concept.state';
import { UpdateJobStatus } from '../../../actions';
import { ActionContributionsService } from '@ansys-internal/awf-angular/contributions';
import { startWith, Subject, takeUntil } from 'rxjs';
import { WorkspaceTabSection } from '@ansys-internal/awf-angular/contributions';
import { StartSolveComponent } from '../../dialogs/start-solve/start-solve.component';
import {
  AnalyticsEventPayload,
  SubmitAnalyticsEventAction,
} from '@ansys-internal/awf-angular/analytics';
import { State } from '@ansys-internal/awf-angular/store';
import {
  DialogService,
  NavigateAction,
} from '@ansys-internal/awf-angular/shared';
import { TreeviewComponent } from '../../widgets/treeview/treeview.component';
import { ArchitectureConfigurationsComponent } from '../../widgets/architecture-configurations/architecture-configurations.component';
import { SectionCardComponent } from '../section-card/section-card.component';
import { ResultItemComponent } from '../../widgets/results-list/result-item/result-item.component';
import {
  PageState,
  PageStateExecuteEnum,
  PageStateModel,
} from '../../../state/lib/app-page.state';
import { CevActionsService } from '../../../shared/services/cev-actions.service';
import { animate, style, transition, trigger } from '@angular/animations';
import {
  CompareJobsExecuteEnum,
  CompareJobsState,
  CompareJobsStateModel,
} from '../../../state/lib/compare-jobs.state';
import { RequirementsSolved } from '../../../../api';
import { LayoutService } from '../../../shared/services/layout.service';
import {JobErrorDialogComponent} from "../../dialogs/job-error-dialog/job-error-dialog.component";
import { JobState } from '../../../state/lib/jobs.state';
import { Job } from '@ansys/cloud-angular-client';

interface ButtonStatus {
  value: string;
  loading: boolean;
  icon: Icons;
}

@Component({
    selector: 'app-left-section',
    imports: [
        ButtonComponent,
        DashboardPanelComponent,
        IconComponent,
        NgIf,
        NgSwitchCase,
        ResultsListComponent,
        NgSwitch,
        CommonModule,
        TreeviewComponent,
        ArchitectureConfigurationsComponent,
        SectionCardComponent,
    ],
    templateUrl: './left-section.component.html',
    styleUrl: './left-section.component.scss',
    animations: [
        trigger('slideInFromRight', [
            transition(':enter', [
                style({
                    opacity: 1,
                    transform: 'translateX(100%)', // Starts off-screen
                    position: 'absolute',
                    right: '0',
                    width: '50%',
                }),
                animate('300ms ease-out', style({
                    opacity: 1,
                    transform: 'translateX(0)', // End in normal position
                    position: 'absolute',
                    right: '0',
                    overflow: 'hidden',
                })),
            ]),
        ]),
    ]
})
export class LeftSectionComponent {
  @Input() public selectedSection!: ConceptSections;
  protected page?: PageStateModel;
  protected readonly iconSize = IconSize;
  protected readonly buttonType = ButtonType;
  protected readonly buttonSize = ButtonSize;
  protected readonly size = Size;
  protected readonly conceptSections = ConceptSections;
  protected readonly subsections = requirementSections;
  protected readonly icons = Icons;
  protected readonly conceptSubSection = ConceptSubSection;
  protected readonly colorBackground = ColorBackground;
  protected readonly colorForeground = ColorForeground;
  protected compareCheckboxesVisible: boolean = false;
  protected cliVersion: string = '';
  protected selectedJobDuration: string = '';
  protected solveRequirementText: ButtonStatus = {
    value: 'Solve',
    loading: false,
    icon: Icons.ONSCALE,
  };
  protected selectedItems: string[] = [];
  private ngUnsubscribe: Subject<boolean> = new Subject<boolean>();
  private readonly statusMap: Map<string, ButtonStatus> = new Map([
    ['CREATING', { value: 'Job Creating', loading: true, icon: Icons.SPINNER }],
  ]);
  protected sectionSelectedAction?: WorkspaceTabSection;
  protected resultsToCompare: RequirementsSolved[] | null = null;
  protected jobsToCompare: any[] | null = null;
  protected maxHeight!: Signal<number>;
  protected readonly isDevMode = environment.appStage === EnvironmentType.LOCAL || environment.appStage === EnvironmentType.DEV;
  protected readonly tabOffsetHeight = 48;
  protected readonly solveHeight = 80 + (this.isDevMode ? 50 : 0);

  constructor(
    private state: State,
    private actions: ActionContributionsService,
    private _cdr: ChangeDetectorRef,
    private dialogService: DialogService,
    private requirementsService: RequirementsService,
    private resultsService: ResultsService,
    private cevAction: CevActionsService,
    private cevActions: CevActionsService,
    private layoutService: LayoutService,
  ) {
    this.maxHeight = this.layoutService.cardHeight;

    this.requirementsService.selectedResultCliVersion
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((cliVersion: any) => {
        this.cliVersion = cliVersion;
      });

    this.actions
      .get(UpdateJobStatus)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((context: [string, string]) => {
        const [, status] = context;
        this.solveRequirementText = this.statusMap.get(status) || {
          value: 'Solve',
          loading: false,
          icon: Icons.ONSCALE,
        };
        this._cdr.detectChanges();
      });
  }

  ngOnInit(): void {
    this.state.get(ActiveSectionState).subscribe((section) => {
      this.sectionSelectedAction = section;
    });

    this.state
      .get(PageState)
      .pipe(
        startWith(this.state.value(PageState)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((page: PageStateModel) => {
        this.page = page;
        this._cdr.detectChanges();
      });

    this.state
      .get(CompareJobsState)
      .pipe(
        startWith(this.state.value(CompareJobsState)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((data: CompareJobsStateModel) => {
        this.resultsToCompare = data.resultsToCompare;
        this.jobsToCompare = data.jobs;
        this._cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }

  protected redirectHandle($event: string): void {
    if ($event === this.page?.subsection) {
      return;
    }

    this.cevAction.execute(
      PageStateExecuteEnum.SET_SUBSECTION,
      $event as ConceptSubSection
    );

    if (this.selectedSection === ConceptSections.REQUIREMENT) {
      const a = this.state.value(ActiveSectionState);

      if (a && a.type === ConceptSections.REQUIREMENT) {
        const params: string[] | undefined =
          $event === ConceptSubSection.MAIN ? [] : ['results'];

        this.actions.execute(NavigateAction, [
          ...(a.userData?.urlTree ?? []),
          ...(params ?? []),
        ]);
        if ($event === ConceptSubSection.MAIN) {
          this.requirementsService.jobResultLoading.next(JobLoadingStatus.IDLE);
        }
      }
    }
  }

  protected toggleCompareCheckboxes(): void {
    this.compareCheckboxesVisible = !this.compareCheckboxesVisible;
    this._cdr.detectChanges();

    if (!this.compareCheckboxesVisible) {
      this.selectedItems = [];
    }
  }

  protected async compareResults(): Promise<void> {
    const section = this.state.value(ActiveSectionState);

    if (section && section.type === ConceptSections.REQUIREMENT) {
      this.actions.execute(NavigateAction, [
        ...(section.userData?.urlTree ?? []),
        'results',
        ConceptSubSection.COMPARISON_TOOL,
        this.selectedItems.join(','),
      ]);
    }
  }

  protected selectAllSimilarResults(): void {
    if (!this.resultsToCompare?.length || !this.jobsToCompare?.length) return;

    const { requirement_solved_type: typeToMatch, requirement } =
      this.resultsToCompare[0];
    const nameToMatch = requirement?.name;
    const existingIds = new Set(this.resultsToCompare.map((r) => r.id));

    const matchingResultIds = new Set<string>();
    const allMatchingResults: RequirementsSolved[] = [];

    for (const job of this.jobsToCompare) {
      for (const result of job.results) {
        if (
          result.requirement_solved_type === typeToMatch &&
          result.requirement?.name === nameToMatch
        ) {
          allMatchingResults.push({ ...result, jobId: job.jobId });
          matchingResultIds.add(result.id);
        }
      }
    }

    const allSelected = [...matchingResultIds].every((id) =>
      existingIds.has(id)
    );

    if (allSelected) {
      for (const result of this.resultsToCompare) {
        if (result.id) {
          this.cevActions.execute(
            CompareJobsExecuteEnum.REMOVE_RESULT_FROM_COMPARE,
            result.id
          );
        }
      }
    } else {
      // Unselect all current results first
      for (const result of this.resultsToCompare) {
        if (result.id) {
          this.cevActions.execute(
            CompareJobsExecuteEnum.REMOVE_RESULT_FROM_COMPARE,
            result.id
          );
        }
      }

      // Then add the matching ones
      const newResultsToAdd = allMatchingResults;
      this.cevActions.execute(
        CompareJobsExecuteEnum.ADD_RESULT_TO_COMPARE,
        newResultsToAdd
      );
    }
  }

  get canSelectAllSimilar(): boolean {
    if (!this.resultsToCompare?.length || !this.jobsToCompare?.length)
      return false;

    const { requirement_solved_type: typeToMatch, requirement } =
      this.resultsToCompare[0];
    const nameToMatch = requirement?.name;
    const existingIds = new Set(this.resultsToCompare.map((r) => r.id));

    let totalMatching = 0;
    let matchingSelected = 0;

    for (const job of this.jobsToCompare) {
      for (const result of job.results) {
        if (
          result.requirement_solved_type === typeToMatch &&
          result.requirement?.name === nameToMatch
        ) {
          totalMatching++;
          if (existingIds.has(result.id)) matchingSelected++;
        }
      }
    }

    return (
      totalMatching > 0 &&
      (matchingSelected > 0 || matchingSelected < totalMatching)
    );
  }

  get selectSimilarButtonLabel(): string {
    if (!this.resultsToCompare?.length || !this.jobsToCompare?.length)
      return 'Select Results with similar names';

    const { requirement_solved_type: typeToMatch, requirement } =
      this.resultsToCompare[0];
    const nameToMatch = requirement?.name;
    const existingIds = new Set(this.resultsToCompare.map((r) => r.id));
    const matchingResultIds = new Set<string>();

    for (const job of this.jobsToCompare) {
      for (const result of job.results) {
        if (
          result.requirement_solved_type === typeToMatch &&
          result.requirement?.name === nameToMatch
        ) {
          matchingResultIds.add(result.id);
        }
      }
    }

    const allSelected = [...matchingResultIds].every((id) =>
      existingIds.has(id)
    );
    return allSelected
      ? 'Deselect All Similar Results'
      : 'Select Results with similar names';
  }

  protected localDevFileName: string | null = null;
  protected loadLocalOutputFile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const json = JSON.parse(text);
        const data = json.solved_requirements ?? json;
        // Push data to the service – results-list subscribes and handles display
        this.resultsService.localDevOutput$.next(data);
        this.localDevFileName = file.name;
        // Navigate to the results subsection so results-list is mounted and picks up the data
        this.navigateToLocalResults();
        this._cdr.detectChanges();
      } catch (e) {
        console.error('Failed to parse output file', e);
      }
    };
    input.click();
  }

  private navigateToLocalResults(): void {
    const section = this.state.value(ActiveSectionState);
    if (section?.id) {
      this.actions.execute(NavigateAction, [
        ...(section.userData?.urlTree ?? []),
        'results',
      ]);
    }
    this.cevAction.execute(PageStateExecuteEnum.SET_SUBSECTION, ConceptSubSection.RESULTS);
  }

  protected solveButtonClicked($event: Event): void {
    $event.preventDefault();
    const tab = this.state.value(ActiveTabState);
    const dialog = this.dialogService.open(StartSolveComponent, {
      title: 'Start Solve',
      data: {
        concept: this.state.value(ActiveConceptState),
        designId: tab?.id || '',
        confirm: (
          name: string,
          ids: string[],
          extraMemory: boolean = false,
          dockerTag: string | undefined
        ) => {
          this.solveRequirementText = {
            value: 'Job Creating',
            loading: true,
            icon: Icons.SPINNER,
          };
          dialog.close();
          this._cdr.detectChanges();

          this.requirementsService
            .solveRequirements(name, ids, extraMemory, dockerTag)
            .finally(() => {
              this.solveRequirementText = {
                value: 'Solve',
                loading: false,
                icon: Icons.ONSCALE,
              };
              this._cdr.detectChanges();
            });

          const eventData: AnalyticsEventPayload = {
            type: AnalyticEventEnum.REQUIREMENT_SOLVE_STARTED,
            data: { name, ids },
          };
          this.actions.execute(SubmitAnalyticsEventAction, eventData);
        },
      },
    });
  }

  onDownloadDetails() {
    const tab = this.state.value(ActiveTabState);

    // Get the selected job from the current URL params (urlParams[1] = jobId when in results section)
    const jobId = this.sectionSelectedAction?.urlParams?.[1];
    const jobs: Job[] = this.state.value(JobState) ?? [];
    const selectedJob = jobs.find((j) => j.jobId === jobId);

    this.dialogService.open(JobErrorDialogComponent, {
      title: 'Downloads',
      data: {
        message: 'Choose which file you want to download',
        jobName: selectedJob?.jobName as string,
        jobId: selectedJob?.jobId as string,
        jobDockerTag: selectedJob?.dockerTag as string,
        designId: tab?.id as string,
      },
    });
  }

  protected readonly Icons = Icons;
}
